import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { CalendarIcon, UserIcon, TagIcon, ChevronLeftIcon, ShareIcon, BookmarkIcon, FacebookIcon, TwitterIcon, LinkedinIcon, MailIcon, ArrowRightIcon, PlayIcon, ClockIcon, MessageSquareIcon, ThumbsUpIcon, DownloadIcon, FileTextIcon } from 'lucide-react';
export const VideoPage = () => {
  const {
    videoId
  } = useParams();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedVideos, setRelatedVideos] = useState([]);
  const [showTranscript, setShowTranscript] = useState(false);
  useEffect(() => {
    // Simulate fetching video data
    const fetchVideo = () => {
      setLoading(true);
      // Mock videos data - in a real app, this would be fetched from an API
      const mockVideos = [{
        id: 'probiotics-hype',
        title: 'Ukweli Talks: Probiotics Beyond the Hype',
        type: 'Video',
        date: 'December 8, 2023',
        author: 'Dr. James Wilson & Dr. Lisa Park',
        authorTitle: 'Microbiome Research Team, Ukweli Institute',
        authorImage: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        image: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        excerpt: 'Our most-watched webinar featuring leading experts discussing what the science really says about probiotics and their impact on health outcomes.',
        topics: ['Probiotics', 'Gut Health'],
        duration: '48:22',
        views: '24,876',
        likes: '1,243',
        description: 'In this in-depth webinar, Dr. James Wilson and Dr. Lisa Park cut through the marketing hype surrounding probiotics to reveal what the scientific evidence actually supports. They explore the most well-researched probiotic strains, discuss the importance of strain-specific effects, and analyze the current state of clinical evidence for various health claims.\n\nThis session covers:\n- How probiotics work in the gut ecosystem\n- The strongest evidence-based applications for probiotics\n- Common misconceptions in probiotic marketing\n- How to evaluate probiotic products for quality and efficacy\n- The future of next-generation probiotics and personalized approaches',
        transcript: [{
          time: '00:00',
          speaker: 'Dr. James Wilson',
          text: "Welcome everyone to this Ukweli Talks session on probiotics. I'm Dr. James Wilson, joined today by my colleague Dr. Lisa Park. We're going to be diving deep into what the science really tells us about probiotics, beyond all the marketing hype you might encounter."
        }, {
          time: '01:15',
          speaker: 'Dr. Lisa Park',
          text: "Thanks James. Before we get into specific products and claims, I think it's important to establish what probiotics actually are. By definition, probiotics are live microorganisms that, when administered in adequate amounts, confer a health benefit on the host. This definition has several important components that we'll unpack throughout this talk."
        }, {
          time: '02:30',
          speaker: 'Dr. James Wilson',
          text: 'Exactly, Lisa. And one of the most critical points is that probiotic effects are strain-specific. This means that different strains, even within the same species, can have completely different effects on the body. This is often overlooked in marketing materials, which might just mention a genus and species like Lactobacillus acidophilus without specifying the exact strain.'
        }, {
          time: '03:45',
          speaker: 'Dr. Lisa Park',
          text: "That's right. And this strain specificity is crucial when we look at the evidence. For example, Lactobacillus rhamnosus GG has been well-studied for preventing antibiotic-associated diarrhea, but other L. rhamnosus strains might not have the same effect. The research is tied to specific strains for specific conditions."
        }, {
          time: '05:10',
          speaker: 'Dr. James Wilson',
          text: "Let's talk about what conditions have the strongest evidence for probiotic benefits. The most robust evidence exists for antibiotic-associated diarrhea, certain forms of infectious diarrhea, and some specific digestive conditions like certain subtypes of IBS."
        }, {
          time: '06:30',
          speaker: 'Dr. Lisa Park',
          text: "And it's worth noting that for many other commonly advertised benefits, the evidence is either preliminary, mixed, or sometimes nonexistent. Claims about probiotics improving immunity, mental health, skin conditions, or weight management often get ahead of what's been proven in rigorous clinical trials."
        }]
      }, {
        id: 'microbiome-explained',
        title: 'Microbiome Research Explained: Interview with Shawn Blake',
        type: 'Video',
        date: 'July 28, 2023',
        author: 'Ukweli Media Team',
        authorTitle: 'Ukweli Institute',
        authorImage: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pHWegwvKPXZhGe1ZwtEHkB/Generated_Image_September_17%2C_2025_-_8_14PM~2.png',
        image: 'https://images.unsplash.com/photo-1551817958-d9d86fb29431?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        excerpt: 'Leading researcher Shawn Blake breaks down complex microbiome concepts into accessible insights.',
        topics: ['Microbiome', 'Research Methods'],
        duration: '36:45',
        views: '18,452',
        likes: '987',
        description: 'In this enlightening interview, Shawn Blake, Founder and Chief Scientific Officer of the Ukweli Institute, explains the fundamentals of microbiome research and its implications for human health. With his extensive background in microbiome science, Blake makes complex scientific concepts accessible to a general audience.\n\nThis interview covers:\n- The basic composition of the human microbiome\n- How modern research techniques are revolutionizing our understanding\n- The connection between microbiome diversity and health outcomes\n- Common misconceptions about microbiome science\n- Promising directions for future research and therapeutic applications',
        transcript: [{
          time: '00:00',
          speaker: 'Interviewer',
          text: "Today we're speaking with Shawn Blake, Founder and Chief Scientific Officer of the Ukweli Institute. Mr. Blake, thank you so much for joining us."
        }, {
          time: '00:15',
          speaker: 'Shawn Blake',
          text: "Thank you for having me. I'm excited to share some insights about microbiome research and why it matters for human health."
        }, {
          time: '00:30',
          speaker: 'Interviewer',
          text: "Let's start with the basics. When we talk about the human microbiome, what exactly are we referring to?"
        }, {
          time: '00:45',
          speaker: 'Shawn Blake',
          text: "The human microbiome refers to the entire collection of microorganisms that live in and on our bodies—bacteria, fungi, viruses, and other microbes. These communities exist throughout the body, but the largest and most diverse is in our gut. What's fascinating is that we have more microbial cells in our body than human cells, and they contain over 100 times more genes than our human genome."
        }]
      }, {
        id: 'fermented-foods',
        title: 'Ukweli Insights: The Science of Fermented Foods',
        type: 'Video',
        date: 'January 25, 2023',
        author: 'Dr. Lisa Park',
        authorTitle: 'Nutritional Microbiology Lead, Ukweli Institute',
        authorImage: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        image: 'https://images.unsplash.com/photo-1563355846-0e8ad5491037?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        excerpt: 'Exploring the microbiological processes and health benefits of traditional fermented foods.',
        topics: ['Nutrition', 'Microbiome'],
        duration: '28:42',
        views: '12,783',
        likes: '756',
        description: 'In this educational video, Dr. Lisa Park explores the fascinating world of fermented foods and their potential impact on gut health and overall wellness. From ancient traditions to modern science, this presentation covers the microbiological processes behind fermentation and the evidence for health benefits.\n\nTopics covered include:\n- The history and cultural significance of fermented foods across different societies\n- The microbiology of fermentation and how beneficial microbes transform food\n- Research on the potential health benefits of kimchi, sauerkraut, kefir, kombucha, and other fermented foods\n- How fermented foods differ from probiotic supplements\n- Practical guidance on incorporating fermented foods into your diet',
        transcript: [{
          time: '00:00',
          speaker: 'Dr. Lisa Park',
          text: "Hello everyone, and welcome to this Ukweli Insights presentation on the science of fermented foods. I'm Dr. Lisa Park, and today we'll explore how these traditional foods are made, how they affect our microbiome, and what the research says about their health benefits."
        }, {
          time: '01:20',
          speaker: 'Dr. Lisa Park',
          text: 'Fermented foods have been part of human diets for thousands of years, across virtually every culture on the planet. From sauerkraut in Eastern Europe to kimchi in Korea, tempeh in Indonesia to kefir in the Caucasus Mountains, humans have been harnessing microbial fermentation long before we understood the science behind it.'
        }]
      }];
      const foundVideo = mockVideos.find(v => v.id === videoId);
      // Find related videos (excluding the current one)
      const related = mockVideos.filter(v => v.id !== videoId && (foundVideo ? v.topics.some(topic => foundVideo.topics.includes(topic)) : false)).slice(0, 3);
      setTimeout(() => {
        setVideo(foundVideo || null);
        setRelatedVideos(related);
        setLoading(false);
      }, 800); // Simulate network delay
    };
    fetchVideo();
  }, [videoId]);
  if (loading) {
    return <div className="min-h-screen pt-32 pb-12 flex flex-col items-center justify-start bg-gray-50" data-id="element-2165">
        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-2166"></div>
        <p className="mt-4 text-gray-600" data-id="element-2167">Loading video...</p>
      </div>;
  }
  if (!video) {
    return <div className="min-h-screen pt-32 pb-12 flex flex-col items-center justify-start bg-gray-50" data-id="element-2168">
        <div className="text-center" data-id="element-2169">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-2170">
            Video Not Found
          </h2>
          <p className="text-gray-600 mb-8" data-id="element-2171">
            The video you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/media" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-2172">
            <ChevronLeftIcon size={16} className="mr-2" data-id="element-2173" />
            Back to Media & Insights
          </Link>
        </div>
      </div>;
  }
  return <div className="bg-white w-full" data-id="element-2174">
      {/* Hero Section */}
      <section className="relative pt-32 pb-12 bg-gray-50" data-id="element-2175">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2176">
          <div className="mb-6" data-id="element-2177">
            <Link to="/media" className="inline-flex items-center text-blue-600 hover:text-blue-800" data-id="element-2178">
              <ChevronLeftIcon size={16} className="mr-1" data-id="element-2179" />
              Back to Media & Insights
            </Link>
          </div>
        </div>
      </section>
      {/* Video Player Section */}
      <section className="bg-gray-900 py-12" data-id="element-2180">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2181">
          <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden shadow-2xl" data-id="element-2182">
            <iframe src={video.videoUrl} title={video.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" data-id="element-2183"></iframe>
          </div>
        </div>
      </section>
      {/* Video Info Section */}
      <section className="py-8 bg-white border-b border-gray-200" data-id="element-2184">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2185">
          <div className="flex flex-wrap items-center gap-2 mb-3" data-id="element-2186">
            {video.topics.map(topic => <span key={topic} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-2187">
                <TagIcon size={12} className="mr-1" data-id="element-2188" />
                {topic}
              </span>)}
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" data-id="element-2189">
            {video.title}
          </h1>
          <div className="flex flex-wrap justify-between items-center mb-6" data-id="element-2190">
            <div className="flex items-center" data-id="element-2191">
              <img src={video.authorImage} alt={video.author} className="w-10 h-10 rounded-full mr-3 object-cover" data-id="element-2192" />
              <div className="p-6" data-id="element-2193">
                <h3 className="text-xl font-semibold text-gray-900" data-id="element-2194">
                  {video.author.includes('Shawn Blake') ? <Link to="/profile" className="hover:text-blue-600 transition-colors" data-id="element-2195">
                      {video.author}
                    </Link> : video.author}
                </h3>
                <p className="text-blue-600 mb-3" data-id="element-2196">{video.authorTitle}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4 mt-4 sm:mt-0" data-id="element-2197">
              <div className="flex items-center text-gray-600" data-id="element-2198">
                <CalendarIcon size={16} className="mr-1" data-id="element-2199" />
                <span className="text-sm" data-id="element-2200">{video.date}</span>
              </div>
              <div className="flex items-center text-gray-600" data-id="element-2201">
                <ClockIcon size={16} className="mr-1" data-id="element-2202" />
                <span className="text-sm" data-id="element-2203">{video.duration}</span>
              </div>
              <div className="flex items-center text-gray-600" data-id="element-2204">
                <PlayIcon size={16} className="mr-1" data-id="element-2205" />
                <span className="text-sm" data-id="element-2206">{video.views} views</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap justify-between items-center py-4 border-t border-b border-gray-200" data-id="element-2207">
            <div className="flex space-x-4" data-id="element-2208">
              <button className="inline-flex items-center text-gray-700 hover:text-blue-600 transition-colors" data-id="element-2209">
                <ThumbsUpIcon size={18} className="mr-2" data-id="element-2210" />
                <span data-id="element-2211">{video.likes}</span>
              </button>
              <button className="inline-flex items-center text-gray-700 hover:text-blue-600 transition-colors" data-id="element-2212">
                <ShareIcon size={18} className="mr-2" data-id="element-2213" />
                Share
              </button>
              <button className="inline-flex items-center text-gray-700 hover:text-blue-600 transition-colors" data-id="element-2214">
                <BookmarkIcon size={18} className="mr-2" data-id="element-2215" />
                Save
              </button>
            </div>
            <div className="mt-4 sm:mt-0" data-id="element-2216">
              <button className="inline-flex items-center text-gray-700 hover:text-blue-600 transition-colors" onClick={() => setShowTranscript(!showTranscript)} data-id="element-2217">
                <FileTextIcon size={18} className="mr-2" data-id="element-2218" />
                {showTranscript ? 'Hide Transcript' : 'Show Transcript'}
              </button>
            </div>
          </div>
        </div>
      </section>
      {/* Video Description and Transcript */}
      <section className="py-8 bg-white" data-id="element-2219">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2220">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2221">
            <div className="md:col-span-2" data-id="element-2222">
              <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2223">
                About this video
              </h2>
              <div className="text-gray-700 whitespace-pre-line mb-8" data-id="element-2224">
                {video.description}
              </div>
              {showTranscript && <div data-id="element-2225">
                  <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2226">
                    Transcript
                  </h2>
                  <div className="bg-gray-50 rounded-lg p-6" data-id="element-2227">
                    {video.transcript.map((segment, index) => <div key={index} className="mb-6" data-id="element-2228">
                        <div className="flex items-center mb-2" data-id="element-2229">
                          <span className="text-sm font-mono text-gray-500 mr-3" data-id="element-2230">
                            {segment.time}
                          </span>
                          <span className="font-medium text-gray-900" data-id="element-2231">
                            {segment.speaker === 'Shawn Blake' ? <Link to="/profile" className="hover:text-blue-600 transition-colors" data-id="element-2232">
                                {segment.speaker}
                              </Link> : segment.speaker}
                          </span>
                        </div>
                        <p className="text-gray-700 pl-16" data-id="element-2233">{segment.text}</p>
                      </div>)}
                  </div>
                </div>}
            </div>
            <div className="md:col-span-1" data-id="element-2234">
              <div className="bg-gray-50 rounded-lg p-6" data-id="element-2235">
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2236">
                  Resources
                </h3>
                <ul className="space-y-3" data-id="element-2237">
                  <li data-id="element-2238">
                    <a href="#" className="flex items-center text-blue-600 hover:text-blue-800" data-id="element-2239">
                      <DownloadIcon size={16} className="mr-2" data-id="element-2240" />
                      Download presentation slides
                    </a>
                  </li>
                  <li data-id="element-2241">
                    <a href="#" className="flex items-center text-blue-600 hover:text-blue-800" data-id="element-2242">
                      <FileTextIcon size={16} className="mr-2" data-id="element-2243" />
                      Related research paper
                    </a>
                  </li>
                  <li data-id="element-2244">
                    <a href="#" className="flex items-center text-blue-600 hover:text-blue-800" data-id="element-2245">
                      <DownloadIcon size={16} className="mr-2" data-id="element-2246" />
                      Download transcript (PDF)
                    </a>
                  </li>
                </ul>
              </div>
              <div className="mt-6 bg-blue-50 rounded-lg p-6" data-id="element-2247">
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2248">
                  Share this video
                </h3>
                <div className="flex space-x-3 mb-4" data-id="element-2249">
                  <a href="#" className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" data-id="element-2250">
                    <FacebookIcon size={18} data-id="element-2251" />
                  </a>
                  <a href="#" className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" data-id="element-2252">
                    <TwitterIcon size={18} data-id="element-2253" />
                  </a>
                  <a href="#" className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" data-id="element-2254">
                    <LinkedinIcon size={18} data-id="element-2255" />
                  </a>
                  <a href="#" className="p-2 bg-gray-700 text-white rounded-full hover:bg-gray-800 transition-colors" data-id="element-2256">
                    <MailIcon size={18} data-id="element-2257" />
                  </a>
                </div>
                <div className="relative" data-id="element-2258">
                  <input type="text" value={`https://ukweli.org/video/${video.id}`} readOnly className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-500" data-id="element-2259" />
                  <button className="absolute right-2 top-1/2 transform -translate-y-1/2 text-blue-600 hover:text-blue-800 text-sm font-medium" data-id="element-2260">
                    Copy
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Related Videos */}
      {relatedVideos.length > 0 && <section className="py-12 bg-gray-50" data-id="element-2261">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2262">
            <h2 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-2263">
              Related Videos
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2264">
              {relatedVideos.map(related => <motion.div key={related.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300" data-id="element-2265">
                  <Link to={`/video/${related.id}`} className="block h-48 overflow-hidden relative" data-id="element-2266">
                    <img src={related.image} alt={related.title} className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" data-id="element-2267" />
                    <div className="absolute inset-0 flex items-center justify-center" data-id="element-2268">
                      <div className="bg-blue-600/80 rounded-full p-3 text-white" data-id="element-2269">
                        <PlayIcon size={24} data-id="element-2270" />
                      </div>
                    </div>
                    <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded" data-id="element-2271">
                      {related.duration}
                    </div>
                  </Link>
                  <div className="p-6" data-id="element-2272">
                    <div className="flex items-center text-sm text-gray-500 mb-2" data-id="element-2273">
                      <CalendarIcon size={14} className="mr-1" data-id="element-2274" />
                      <span data-id="element-2275">{related.date}</span>
                    </div>
                    <Link to={`/video/${related.id}`} data-id="element-2276">
                      <h3 className="font-bold text-lg mb-2 text-gray-900 hover:text-blue-600 transition-colors" data-id="element-2277">
                        {related.title}
                      </h3>
                    </Link>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2" data-id="element-2278">
                      {related.excerpt}
                    </p>
                    <Link to={`/video/${related.id}`} className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium text-sm" data-id="element-2279">
                      Watch video <ArrowRightIcon size={16} className="ml-1" data-id="element-2280" />
                    </Link>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>}
      {/* Comments Section - could be implemented in a real app */}
      <section className="py-12 bg-white" data-id="element-2281">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2282">
          <div className="flex items-center mb-8" data-id="element-2283">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-2284">Comments</h2>
            <div className="ml-3 bg-gray-100 text-gray-700 text-sm px-2 py-1 rounded" data-id="element-2285">
              24
            </div>
          </div>
          <div className="flex items-start mb-8" data-id="element-2286">
            <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80" alt="Your profile" className="w-10 h-10 rounded-full mr-4 object-cover" data-id="element-2287" />
            <div className="flex-grow" data-id="element-2288">
              <textarea placeholder="Add a comment..." className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" rows={3} data-id="element-2289"></textarea>
              <div className="mt-2 flex justify-end" data-id="element-2290">
                <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-2291">
                  Comment
                </button>
              </div>
            </div>
          </div>
          <div className="space-y-8" data-id="element-2292">
            {/* Sample comments - would be dynamic in a real app */}
            <div className="flex items-start" data-id="element-2293">
              <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80" alt="Commenter" className="w-10 h-10 rounded-full mr-4 object-cover" data-id="element-2294" />
              <div data-id="element-2295">
                <div className="flex items-center mb-1" data-id="element-2296">
                  <h4 className="font-medium text-gray-900 mr-2" data-id="element-2297">
                    Elena Martinez
                  </h4>
                  <span className="text-sm text-gray-500" data-id="element-2298">2 days ago</span>
                </div>
                <p className="text-gray-700 mb-2" data-id="element-2299">
                  This was incredibly informative! I've been taking probiotics
                  for years without really understanding the strain-specific
                  effects. Would love to see more content about how to evaluate
                  probiotic products.
                </p>
                <div className="flex items-center text-sm" data-id="element-2300">
                  <button className="text-gray-500 hover:text-gray-700 mr-4" data-id="element-2301">
                    <ThumbsUpIcon size={14} className="inline mr-1" data-id="element-2302" /> 12
                  </button>
                  <button className="text-gray-500 hover:text-gray-700" data-id="element-2303">
                    Reply
                  </button>
                </div>
              </div>
            </div>
            <div className="flex items-start" data-id="element-2304">
              <img src="https://images.unsplash.com/photo-1599566150163-29194dcaad36?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80" alt="Commenter" className="w-10 h-10 rounded-full mr-4 object-cover" data-id="element-2305" />
              <div data-id="element-2306">
                <div className="flex items-center mb-1" data-id="element-2307">
                  <h4 className="font-medium text-gray-900 mr-2" data-id="element-2308">
                    Michael Chen
                  </h4>
                  <span className="text-sm text-gray-500" data-id="element-2309">3 days ago</span>
                </div>
                <p className="text-gray-700 mb-2" data-id="element-2310">
                  Dr. Wilson and Dr. Park explain complex concepts so clearly. I
                  appreciate the honesty about where the evidence is strong vs.
                  where more research is needed. This is the kind of science
                  communication we need!
                </p>
                <div className="flex items-center text-sm" data-id="element-2311">
                  <button className="text-gray-500 hover:text-gray-700 mr-4" data-id="element-2312">
                    <ThumbsUpIcon size={14} className="inline mr-1" data-id="element-2313" /> 8
                  </button>
                  <button className="text-gray-500 hover:text-gray-700" data-id="element-2314">
                    Reply
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-8 text-center" data-id="element-2315">
            <button className="text-blue-600 hover:text-blue-800 font-medium" data-id="element-2316">
              Load more comments
            </button>
          </div>
        </div>
      </section>
      {/* Newsletter Sign Up */}
      <section className="py-12 bg-gray-50" data-id="element-2317">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2318">
          <div className="bg-blue-600 rounded-xl p-8 md:p-12 text-center" data-id="element-2319">
            <h2 className="text-2xl font-bold text-white mb-4" data-id="element-2320">
              Subscribe to Our Channel
            </h2>
            <p className="text-blue-100 mb-6" data-id="element-2321">
              Get notified about new videos, webinars, and educational content
              from the Ukweli Institute
            </p>
            <div className="max-w-md mx-auto" data-id="element-2322">
              <form className="flex flex-col sm:flex-row gap-2" data-id="element-2323">
                <input type="email" placeholder="Your email address" className="flex-grow px-4 py-2 border border-transparent rounded-md focus:ring-white focus:border-white" required data-id="element-2324" />
                <button type="submit" className="bg-white text-blue-600 px-6 py-2 rounded-md hover:bg-blue-50 transition-colors whitespace-nowrap font-medium" data-id="element-2325">
                  Subscribe
                </button>
              </form>
              <p className="text-xs text-blue-100 mt-2" data-id="element-2326">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
};